import ArrowSearchIcon from '@mdi/svg/svg/arrow-right.svg?react';
import ClearIcon from '@mdi/svg/svg/close.svg?react';
import AiIcon from '@mdi/svg/svg/creation.svg?react';
import { useUserSession } from '@solinkcorp/authentication';
import { Flex, LoadingSpinner, MDIIcon, toast } from '@solinkcorp/solink-components';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import { FocusIcon } from 'lucide-react';
import React from 'react';
import CloudAiApi from 'src/clients/CloudAiApi';
import useLocale from 'src/hooks/useLocale';
import useVideoSearchImageUpload from '../useVideoSearchImageUpload';
import SearchImagePanel from './SearchImagePanel';
import styles from './SmartSearch.module.sass';

interface SmartSearchProps {
  navigate: (path: string) => void;
  /** b64 encoded string from cloud-ai */
  croppedImage?: string;
  initialSearch?: string;
  /** s3:// URI of the image to seed an active image search (from the URL). */
  initialImageUri?: string;
  loadingResults?: boolean;
  className?: string;
  /** Notifies the parent when an image upload starts/finishes so the results area can render a loading state. */
  onUploadingChange?: (isUploading: boolean) => void;
}

interface ActiveImage {
  /** Canonical s3:// URI once uploaded; null while the upload is in flight. */
  imageUri: string | null;
  /** URL used to render the thumbnail; null while a display URL is being resolved. */
  previewUrl: string | null;
}

interface ThumbnailLoadingArgs {
  /** The active image, or null when there's no image search. */
  image: ActiveImage | null;
  /** An upload is in flight. */
  isUploading: boolean;
  /** The thumbnail <img> has finished downloading. */
  thumbnailLoaded: boolean;
  /** Resolving the display URL or loading the thumbnail failed. */
  thumbnailError: boolean;
  /**
   * A crop search is running but its cropped snapshot hasn't returned yet, so there's
   * no image to render in the thumbnail slot - just the spinner.
   */
  isCropLoading: boolean;
}

/**
 * Whether to show the loading spinner in the thumbnail slot. True while:
 *  - a crop search is running but its snapshot hasn't returned yet (no image to render),
 *  - the initial upload is in flight,
 *  - we're minting a display URL for an imageUri seeded from the URL (no previewUrl yet),
 *  - the resolved thumbnail image is still downloading.
 * An error (display URL failed or the image couldn't load) stops the indicator.
 */
function getIsThumbnailLoading({
  image,
  isUploading,
  thumbnailLoaded,
  thumbnailError,
  isCropLoading,
}: ThumbnailLoadingArgs): boolean {
  if (isCropLoading) {
    return true;
  }
  if (!image || thumbnailError) {
    return false;
  }
  return isUploading || !image.previewUrl || !thumbnailLoaded;
}

export default function SmartSearch(props: SmartSearchProps) {
  const {
    loadingResults,
    navigate,
    initialSearch,
    initialImageUri,
    croppedImage,
    className,
    onUploadingChange,
  } = props;
  // The cropSnapshot endpoint returns a raw base64 JPEG; render it inline as a
  // data URI. Undefined when this isn't a crop search.
  const cropPreviewUrl = croppedImage ? `data:image/jpeg;base64,${croppedImage}` : undefined;
  const [search, setSearch] = React.useState(initialSearch || '');
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [image, setImage] = React.useState<ActiveImage | null>(() =>
    initialImageUri ? { imageUri: initialImageUri, previewUrl: null } : null,
  );
  // Outcome of the most recent thumbnail load, tagged with the preview URL it belongs to.
  // We derive the load/error flags from this (comparing against the *current* previewUrl)
  // instead of resetting booleans in an effect - an effect runs after paint and can clobber
  // a cached image's onLoad that already fired, leaving the spinner stuck.
  const [thumbnailOutcome, setThumbnailOutcome] = React.useState<{
    url: string;
    ok: boolean;
  } | null>(null);
  // The imageUri whose display-URL resolution failed (no previewUrl to tag against).
  const [displayUrlFailedUri, setDisplayUrlFailedUri] = React.useState<string | null>(null);

  const [query] = useQueryParams();
  // A crop search is identified by the URL query params carrying a crop object (matching
  // SidekickPage's cropQuery). While that's true but the cropped snapshot hasn't arrived
  // (croppedImage undefined) the search is still resolving its thumbnail. The actual search
  // results live outside this component, so we lean on `loadingResults` to know the search is
  // still in flight - once it's false (results in, or no search running) we stop the spinner
  // even if the snapshot never came back.
  const isCropSearch = !!query.get('crop') && !!query.get('cameraId');
  const isCropLoading = isCropSearch && !croppedImage && !!loadingResults;

  const locale = useLocale();
  const userSession = useUserSession();
  const token = userSession?.accessToken;
  const { status: uploadStatus, upload, reset } = useVideoSearchImageUpload();

  const formRef = React.useRef<HTMLFormElement>(null);
  const objectUrlRef = React.useRef<string | null>(null);
  /** Bumped when an in-flight upload should be ignored (cleared or superseded). */
  const uploadSessionRef = React.useRef(0);

  const revokeObjectUrl = React.useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  // Revoke any in-memory object URL when the component unmounts.
  React.useEffect(() => revokeObjectUrl, [revokeObjectUrl]);

  // On reload / back-forward we may have an imageUri (from the URL) but no display URL
  // (navigation state is gone). Resolve a fresh display URL so the thumbnail can render.
  React.useEffect(() => {
    if (!image?.imageUri || image.previewUrl || !token) {
      return;
    }
    let cancelled = false;
    if (image.imageUri) {
      CloudAiApi.getImageDisplayUrl(token, image.imageUri)
        .then((displayUrl) => {
          if (!cancelled) {
            setImage((prev) => (prev?.imageUri ? { ...prev, previewUrl: displayUrl } : prev));
          }
        })
        .catch((e) => {
          console.error('Failed to resolve image display URL', e);
          if (!cancelled) {
            setDisplayUrlFailedUri(image.imageUri);
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [image?.imageUri, image?.previewUrl, token]);

  // Keep local state in sync with the URL-derived props. On history navigation
  // (back/forward) the query params change without remounting this component, so the
  // `initial*` props update and we must reflect them - otherwise a stale thumbnail or
  // search term lingers while Sidekick runs the query from the URL.
  React.useEffect(() => {
    setSearch(initialSearch || '');
  }, [initialSearch]);

  React.useEffect(() => {
    setImage((prev) => {
      // Already showing this image (e.g. our own just-completed upload) - keep the
      // resolved preview rather than clobbering it and re-fetching a display URL.
      if ((prev?.imageUri ?? '') === (initialImageUri ?? '')) {
        return prev;
      }
      return initialImageUri ? { imageUri: initialImageUri, previewUrl: null } : null;
    });
  }, [initialImageUri]);

  // Seed the preview with the cropped snapshot once it arrives (crop search has no
  // uploaded imageUri - the crop geometry in the URL drives the actual search, this
  // is display-only). When the crop clears from the URL we drop the data-URI preview
  // but leave any uploaded image untouched.
  React.useEffect(() => {
    setImage((prev) => {
      if (!cropPreviewUrl) {
        return prev?.imageUri === null && prev.previewUrl?.startsWith('data:') ? null : prev;
      }
      if (prev?.previewUrl === cropPreviewUrl) {
        return prev;
      }
      return { imageUri: null, previewUrl: cropPreviewUrl };
    });
  }, [cropPreviewUrl]);

  const clearImage = React.useCallback(() => {
    uploadSessionRef.current += 1;
    revokeObjectUrl();
    setImage(null);
    reset();
  }, [revokeObjectUrl, reset]);

  const submitForm = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (image && !search) {
        // Image search. If the upload is still in flight (`imageUri` not resolved yet)
        // do nothing - handleSelectImage navigates on completion. Falling through to a
        // text search here would navigate to an empty query that the finishing upload
        // would then overwrite.
        if (image.imageUri) {
          navigate(`/sidekick?imageUri=${encodeURIComponent(image.imageUri)}`);
        }
        return;
      }
      if (search) {
        navigate(`/sidekick?search=${encodeURIComponent(search)}`);
      }
    },
    [search, image, navigate],
  );

  const handleSelectImage = React.useCallback(
    async (file: File) => {
      const sessionId = uploadSessionRef.current + 1;
      uploadSessionRef.current = sessionId;

      setSearch('');

      revokeObjectUrl();
      const objectUrl = URL.createObjectURL(file);
      objectUrlRef.current = objectUrl;
      setImage({ imageUri: null, previewUrl: objectUrl });

      // We can't run the image search until the upload mints an imageUri, but leaving the
      // old query in the URL means SidekickPage keeps showing the previous query's results
      // and toolbar while the bar already shows the new image. Strip the stale params now
      // so the results area resets; the sync effect keeps the in-flight thumbnail.
      const hadQuery = !!image?.imageUri || !!initialImageUri || !!search;
      if (hadQuery) {
        navigate('/sidekick');
      }

      const target = await upload(file);
      if (uploadSessionRef.current !== sessionId) {
        URL.revokeObjectURL(objectUrl);
        if (objectUrlRef.current === objectUrl) {
          objectUrlRef.current = null;
        }
        return;
      }

      if (!target) {
        clearImage();
        toast.error(locale.videoSearch.imageUploadError);
        return;
      }

      revokeObjectUrl();
      setImage({ imageUri: target.imageUri, previewUrl: target.displayUrl });
      navigate(`/sidekick?imageUri=${encodeURIComponent(target.imageUri)}`);
    },
    [upload, clearImage, revokeObjectUrl, navigate, locale, image, initialImageUri, search],
  );

  const handleTextChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Typing alongside a selected image is allowed; the thumbnail dims (see the
    // `--dimmed` modifier) to signal the text search will take over on submit.
    setSearch(e.target.value);
  }, []);

  const handleClear = React.useCallback(() => {
    clearImage();
    setSearch('');
  }, [clearImage]);

  // Close the panel when clicking outside the search bar.
  React.useEffect(() => {
    if (!panelOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [panelOpen]);

  const isUploading = uploadStatus === 'uploading';
  // Surface the upload status to the parent so the results area can show a loading
  // indicator while the image is in flight (before any search has actually started).
  React.useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);
  // Derive the load/error flags from the recorded outcome by matching it to the *current*
  // source - a stale outcome (different previewUrl) simply doesn't match, so the flags reset
  // during render with no effect that could race a cached image's onLoad.
  const currentOutcome =
    thumbnailOutcome && thumbnailOutcome.url === image?.previewUrl ? thumbnailOutcome : null;
  const thumbnailLoaded = currentOutcome?.ok === true;
  const thumbnailError =
    currentOutcome?.ok === false ||
    (!!image && !image.previewUrl && displayUrlFailedUri === image.imageUri);
  const isThumbnailLoading = getIsThumbnailLoading({
    image,
    isUploading,
    thumbnailLoaded,
    thumbnailError,
    isCropLoading,
  });
  const showClearButton = !!search || !!image;
  // Captured as a const so the load/error handlers below tag the outcome with a non-null URL.
  const previewUrl = image?.previewUrl;

  return (
    <form
      ref={formRef}
      onSubmit={submitForm}
      className={`${styles['smart-search-form']} ${className}`}
    >
      <MDIIcon
        className={styles['smart-search-icon']}
        icon={AiIcon}
        size={18}
        fill="var(--color-turquoise)"
      />
      {(image || isCropLoading) && (
        <button
          type="button"
          data-testid="smart-search-image-thumbnail"
          title={locale.videoSearch.imageSearchTitle}
          className={styles['smart-search-thumbnail']}
          onClick={() => setPanelOpen(true)}
        >
          {previewUrl && !thumbnailError && (
            <img
              src={previewUrl}
              alt="search"
              onLoad={() => setThumbnailOutcome({ url: previewUrl, ok: true })}
              onError={() => setThumbnailOutcome({ url: previewUrl, ok: false })}
              className={`${styles['smart-search-thumbnail-img']} ${
                isThumbnailLoading ? styles['smart-search-thumbnail-img--uploading'] : ''
              } ${search ? styles['smart-search-thumbnail-img--dimmed'] : ''}`}
            />
          )}
          {isThumbnailLoading && (
            <span className={styles['smart-search-thumbnail-overlay']}>
              <LoadingSpinner size={14} />
            </span>
          )}
        </button>
      )}
      <input
        type="text"
        data-testid="smart-search-input"
        placeholder={
          image
            ? locale.videoSearch.smartSearchActivePlaceholder
            : locale.videoSearch.smartSearchPlaceholder
        }
        value={search}
        onFocus={() => setPanelOpen(true)}
        onClick={() => setPanelOpen(true)}
        onChange={handleTextChange}
        className={`${styles['smart-search-input']} ${
          image || isCropLoading ? styles['smart-search-input--with-image'] : ''
        }`}
      />
      {loadingResults && !isCropSearch ? (
        <div className={styles['smart-search-spinner-wrapper']}>
          <LoadingSpinner size={20} />
        </div>
      ) : (
        <Flex
          alignItems="center"
          gap="0.125rem"
          className={styles['smart-search-button-wrapper']}
          position="absolute"
          width="auto"
        >
          <button
            title="Focus"
            type="button"
            className={`${styles['smart-search-clear-button']} ${styles['smart-search-focus-button']}`}
            onClick={() => setPanelOpen(true)}
          >
            <MDIIcon icon={FocusIcon} size={18} className={styles['smart-search-focus-icon']} />
          </button>
          {showClearButton && (
            <button
              title="clear"
              type="button"
              data-testid="smart-search-clear-button"
              className={styles['smart-search-clear-button']}
              onClick={handleClear}
            >
              <MDIIcon icon={ClearIcon} size={18} />
            </button>
          )}
          <button title="Search" type="submit" className={styles['smart-search-button']}>
            <MDIIcon icon={ArrowSearchIcon} size={18} style={{ margin: 0 }} />
          </button>
        </Flex>
      )}
      {panelOpen && !isUploading && (
        <SearchImagePanel
          onSelect={handleSelectImage}
          previewUrl={
            image?.imageUri || (!!cropPreviewUrl && image?.previewUrl === cropPreviewUrl)
              ? image?.previewUrl
              : null
          }
          onClear={handleClear}
        />
      )}
    </form>
  );
}
